import { Button, Container, FormControlLabel, Grid, Radio, TextField } from '@mui/material';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { cloneDeep } from 'lodash';
import * as _ from 'lodash';
import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { useProgress } from '@/contexts/ProgressProvider';
import VnModalBody from '@/modals/components/VnModalBody';
import VnModalFooter from '@/modals/components/VnModalFooter';
import { useModalContext } from '@/modals/VNModalProvider';

import type { LinkedFileInfo } from '../../../models/item';
import { FuelPortalService } from '../../../services';
import { useAppDispatch } from '../../../store';
import { AssetForm } from '../../../store/forms';
import { applicationDataSelector } from '../../../store/slices/application-data';
import { assetEditorSelector, CreatAssetForm, UpdateAssetForm } from '../../../store/slices/asset-editor';
import { fuelUserAccessSliceSelector } from '../../../store/slices/fuelUserAccess';
import { startJob } from '../../../store/slices/saveAssetJobsState/SaveAssetJobsState';
import useAppUtils from '../../../utils/appUtils';

// List the following languages in the list;
// English [ en-US ]
// Arabic  [ ar-SA ]
// Dutch  [ nl-NL ]
// French  [ fr-FR ]
// German  [ de-DE ]
// Hindi  [ hi-IN ]
// Italian [ it - IT ]
// Spanish [ es-US ]
export const DEFAULT_EXTERNAL_SUBTITLES_LIST: LinkedFileInfo[] = [
  {
    description: 'en-US',
    publicUrl: null,
    previewUri: null,
    s3Path: null,
    fileExtension: null,
  },
  {
    description: 'ar-SA',
    publicUrl: null,
    previewUri: null,
    s3Path: null,
    fileExtension: null,
  },
  {
    description: 'nl-NL',
    publicUrl: null,
    previewUri: null,
    s3Path: null,
    fileExtension: null,
  },
  {
    description: 'fr-FR',
    publicUrl: null,
    previewUri: null,
    s3Path: null,
    fileExtension: null,
  },
  {
    description: 'de-DE',
    publicUrl: null,
    previewUri: null,
    s3Path: null,
    fileExtension: null,
  },
  {
    description: 'hi-IN',
    publicUrl: null,
    previewUri: null,
    s3Path: null,
    fileExtension: null,
  },
  {
    description: 'it-IT',
    publicUrl: null,
    previewUri: null,
    s3Path: null,
    fileExtension: null,
  },
  {
    description: 'es-US',
    publicUrl: null,
    previewUri: null,
    s3Path: null,
    fileExtension: null,
  },
];

const getMergedExternalSubtitles = (defaultExternalSubtitles: LinkedFileInfo[], assetForm) => {
  // console.log('getMergedExternalSubtitles', { assetForm, defaultExternalSubtitles });
  const mergedSubtitles: LinkedFileInfo[] = _.cloneDeep(defaultExternalSubtitles);
  const mySubtitles: LinkedFileInfo[] =
    assetForm &&
    assetForm.originalAssetData &&
    assetForm.originalAssetData.data &&
    assetForm.originalAssetData.data.externalSubtitles
      ? assetForm.originalAssetData.data.externalSubtitles
      : [];

  if (mySubtitles.length > 0) {
    for (let i = 0; i < mySubtitles.length; i++) {
      const foundIndex: LinkedFileInfo = _.findIndex(mergedSubtitles, (s) => s.description === mySubtitles[i].description);
      if (foundIndex > -1) {
        mergedSubtitles[foundIndex] = mySubtitles[i];
      } else {
        // just in case there is a new language subtitle
        mergedSubtitles.push(mySubtitles[i]);
      }
    }
  }
  // console.log('getMergedExternalSubtitles', { mergedSubtitles });
  return mergedSubtitles;
};

interface IPros {
  // open: boolean;
  // assetForm: AssetForm;
}

const CloseCaptionModal = () => {
  const { vnConfirm, alert } = useAppUtils();
  const dispatch = useAppDispatch();
  const { onClose } = useModalContext();
  const { assetForm } = useSelector(assetEditorSelector);
  const { ouData, categories } = useSelector(applicationDataSelector);
  const flags = useFlags();
  const { permissions } = useSelector(fuelUserAccessSliceSelector);
  const [isEditMode, setIsEditMode] = useState(false);
  const [language, setLanguage] = useState('');
  const [uploadLanguage, setUploadLanguage] = useState('');
  const [fileText, setFileText] = useState('');
  const fileInput = useRef<HTMLInputElement | null>(null);
  const [file, setFile] = useState(null);
  const [externalSubtitles, setExternalSubtitles] = useState<LinkedFileInfo[]>([]);
  const [showAutoCreateCaptionsButton, setShowAutoCreateCaptionsButton] = useState(false);
  const { setProgress } = useProgress();

  const handleClose = () => {
    if (onClose) onClose();
    reset();
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    // console.log('handleChange', { event, externalSubtitles });
    const selectedLanguage = event.target.value;
    setLanguage(selectedLanguage);
    const sIndex = _.findIndex(externalSubtitles, (s) => s.description === selectedLanguage);
    // console.log('handleChange', { sIndex });
    setIsEditMode(false);
    loadCaptions(sIndex);
  };

  const reset = () => {
    setFileText('');
    // setExternalSubtitles([]);
    setIsEditMode(false);
    setLanguage('');
    const form = AssetForm.create(permissions, ouData, true);
    dispatch(CreatAssetForm(form));
  };

  const init = () => {
    // console.log('init', { assetForm, flags });

    const mergedSubtitles = getMergedExternalSubtitles(DEFAULT_EXTERNAL_SUBTITLES_LIST, assetForm);
    // console.log('init', { mergedSubtitles });
    setExternalSubtitles(mergedSubtitles);
    setLanguage(mergedSubtitles[0].description);

    const firstSubtileWithCaptions = _.findIndex(mergedSubtitles, (s) => s.publicUrl);
    if (firstSubtileWithCaptions > -1) {
      loadCaptions(firstSubtileWithCaptions, mergedSubtitles);
      setLanguage(mergedSubtitles[firstSubtileWithCaptions].description);
    }
  };

  const onRefreshClick = async () => {
    setProgress(true, 'Please wait...');
    await refresh();
    setProgress(false);
  };

  const refresh = async () => {
    // console.log('refresh', { assetForm });
    await createAssetForm(assetForm.id);
  };

  const allowCreateCaptions = () => {
    let shouldEnableCreateCaptions = false;
    if (
      assetForm.originalAssetData &&
      assetForm.originalAssetData.data &&
      ouData &&
      ouData.metaData &&
      ouData.metaData.showCreateCCButton
    ) {
      const originalAssetData = assetForm.originalAssetData.data;
      const extension = getExtension(originalAssetData.originalFileName);
      if (
        (extension !== 'mp4' &&
          originalAssetData.videoProxy &&
          originalAssetData.videoProxy.s3Path &&
          originalAssetData.ingestStatus === 'Completed') ||
        (extension === 'mp4' && originalAssetData.ingestStatus === 'Completed')
      ) {
        shouldEnableCreateCaptions = true;
      }
    }
    // console.log('shouldEnableCreateCaptions', { shouldEnableCreateCaptions });
    return shouldEnableCreateCaptions;
  };

  const getExtension = (str: string): string => {
    if (!str) return null;
    return str.substring(str.lastIndexOf('.') + 1).toLowerCase();
  };

  const loadCaptions = async (index: number, myExternalSubtitles: LinkedFileInfo = null) => {
    console.log('loadCaptions', { index });
    const mySubtitles = myExternalSubtitles || _.cloneDeep(externalSubtitles);
    // console.log('loadCaptions', { mySubtitles });
    let captionsText = 'No captions available.';
    if (mySubtitles && mySubtitles.length > 0) {
      const { fileText } = mySubtitles[index];
      if (fileText) {
        captionsText = fileText;
      } else {
        const url = mySubtitles[index].publicUrl;
        if (url) {
          const response = await fetch(url);
          if (response) {
            const newText = await response.text();
            captionsText = newText;
            mySubtitles[index].fileText = newText;
            setExternalSubtitles(mySubtitles);
          }
        }
      }
    }
    console.log('captionsText', { captionsText });
    setFileText(captionsText);
  };

  const editCaptions = async (index: number) => {
    setLanguage(externalSubtitles[index].description);
    setIsEditMode(true);
    await loadCaptions(index);
  };

  const deleteCaptions = async (index: number) => {
    const isOk = await vnConfirm('Are you sure you want to delete this captions file?');
    if (isOk) {
      const form: AssetForm = cloneDeep(assetForm);
      if (externalSubtitles && externalSubtitles.length && index > -1) {
        const asset = form.originalAssetData;
        const languageCode = externalSubtitles[index].description;
        onDeleteCaptions(asset, languageCode);
      }
    }
  };

  const onCaptionUpload = async (form: AssetForm, languageCode) => {
    // console.log('onCaptionUpload', { form, languageCode });
    setProgress(true, 'Please wait...');
    const startJobResults = await dispatch(startJob({ form, jobType: 'Subtitle', extraParams: { languageCode } }));
    // console.log('onCaptionUpload', { startJobResults });

    form.ingestFile = '';
    if (!startJobResults || (startJobResults.payload && startJobResults.payload.error && startJobResults.payload.error.stack)) {
      alert('There was an error creating captions.  Please contact support.', 'error');
      setProgress(false);
    } else {
      alert('Captions updated. Please refresh momentarily to see the updated captions.');
      setTimeout(() => {
        refresh();
        setTimeout(() => {
          setProgress(false);
        }, 1000);
      }, 5000);
    }
  };

  const onDeleteCaptions = async (asset, languageCode) => {
    // console.log('onDeleteCaptions', { asset, languageCode });
    if (!asset) {
      return;
    }
    setProgress(true, 'Removing...');

    const foundIndex = _.findIndex(
      asset.data.externalSubtitles,
      (s) => s.description.toLowerCase() === languageCode.toLowerCase()
    );
    if (foundIndex === -1) {
      alert('Subtitle not found.  Please refresh.', 'error');
      setProgress(false);
      return;
    }
    asset.data.externalSubtitles.splice(foundIndex, 1);

    try {
      // aalt 2023-10-18 need to pass Metadata.ClipHLSData to save external subtitles
      await FuelPortalService.updateAsset(asset, ['Metadata.ClipHLSData']);
    } catch (error) {
      alert('Error occured while deleting subtitles.  Please try again or contact system admin.', 'error');
      setProgress(false);
      return;
    }

    const mySubtitles: LinkedFileInfo[] = _.cloneDeep(externalSubtitles);
    const sFoundIndex = _.findIndex(mySubtitles, (s) => s.description.toLowerCase() === languageCode.toLowerCase());
    if (sFoundIndex > -1) {
      mySubtitles[sFoundIndex].publicUrl = null;
      mySubtitles[sFoundIndex].fileExtension = null;
      mySubtitles[sFoundIndex].fileText = null;
      mySubtitles[sFoundIndex].previewUri = null;
      mySubtitles[sFoundIndex].s3Path = null;

      setExternalSubtitles(externalSubtitles);
    }
    if (language === languageCode) {
      setFileText('');
    }

    alert('Captions deleted successfully. Please refresh to see the updated captions.');
    setTimeout(() => {
      refresh();
      setTimeout(() => {
        setProgress(false);
      }, 1000);
    }, 0);
  };

  const createAssetForm = async (id: string): Promise<boolean> => {
    try {
      // console.log('createAssetForm', { id });
      // aalt added id null check - most likely component has been unmounted already in case of ingest caption upload
      if (id) {
        const asset = await FuelPortalService.getClipById(id);
        if (asset) {
          const assetForm = await AssetForm.createFromData(asset, permissions, ouData);
          dispatch(UpdateAssetForm(assetForm));
          return true;
        }
      }
      return false;
    } catch (ex) {
      alert('Error fetching asset.', 'error');
      console.error('Error fetching asset', ex);
      return false;
    }
  };

  const onBrowseButtonClick = (evt, subtitle) => {
    evt.preventDefault();
    // `current` points to the mounted file input element
    // console.log('onBrowseButtonClick', { evt, subtitle });
    if (subtitle.publicUrl) {
      alert('Please delete the current captions before uploading new captions.', 'warning');
      return;
    }
    setUploadLanguage(subtitle.description);
    if (fileInput && fileInput.current) {
      fileInput.current.click();
    }
  };

  const cancel = async () => {
    const isOk = await vnConfirm('Are you sure you want to cancel?  All changes will be lost.');
    if (!isOk) {
      return;
    }
    setIsEditMode(false);
    let sIndex = _.findIndex(externalSubtitles, (s) => s.description === language);
    if (sIndex < -1) {
      sIndex = 0;
    }
    loadCaptions(sIndex);
  };

  const saveCaptions = async () => {
    const currentLanguage = _.cloneDeep(language);
    // console.log('saveCaptions', { currentLanguage });
    const distinctFileName = `subtitle_${new Date().getTime}.webvtt`;
    // console.log('saveCaptions', distinctFileName);
    const file = new File([fileText], distinctFileName, { type: 'application/text' });
    await onFileUpload(file, currentLanguage);
    setIsEditMode(false);

    setLanguage(currentLanguage);
    const sIndex = _.findIndex(externalSubtitles, (s) => s.description === currentLanguage);
    if (sIndex > -1) {
      const mySubtitles = _.cloneDeep(externalSubtitles);
      mySubtitles[sIndex].fileText = fileText;
      setExternalSubtitles(mySubtitles);
    }
    // console.log('handleChange', { sIndex });
    // loadCaptions(sIndex);
  };

  const processFileChange = async (fileList: FileList) => {
    // console.log('processFileChange', { fileList });
    const languageCode = uploadLanguage;
    // console.log('processFileChange', { languageCode });
    if (fileList.length === 0) return;

    // only supporting single file upload...multiple files are possible with the drag and drop, so just take the first file...
    const allowedFiles = ['.vtt', '.webvtt', '.srt'];
    const allowedFilesExtension = /(\.vtt|\.webvtt|\.srt)$/i;
    if (fileList.length === 0) return;

    const message = allowedFiles.join(', ');
    const bodyText = `Please upload files having extensions: ${message} only.`;
    const file = fileList.item(0);
    if (file && !allowedFilesExtension.exec(file && file.name)) {
      alert(bodyText, 'error');
      return;
    }
    // console.log('file', file);
    setFile(file);
    const reader = new FileReader();
    reader.readAsText(file, 'UTF-8');
    // reader.onload = (evt) => {
    //   setFileText(evt.target.result);
    //   // console.log(' evt.target.result', evt.target.result)
    //   // evt.target.result
    // };
    reader.onerror = (evt) => {
      console.error('Unable to read file', evt);
    };
    await onFileUpload(file, languageCode);
    if (fileInput && fileInput.current) fileInput.current.value = '';
  };

  const onFileUpload = async (file: File, languageCode: string) => {
    // console.log('onFileUpload', { file, languageCode });
    const form: AssetForm = cloneDeep(assetForm);
    form.ingestFile = file;
    // form.originalAssetData.data.externalSubtitles = externalSubtitles;
    await onCaptionUpload(form, languageCode);
  };

  const createCaption = async () => {
    const isOk = await vnConfirm('Confirming you are initiating captions for the same language as the video?');
    if (isOk) {
      // console.log('create');
      const myAllowCreateCaptions = allowCreateCaptions();
      if (!myAllowCreateCaptions) {
        const extension = getExtension(assetForm.originalAssetData?.data.originalFileName);
        let message = 'Please wait until asset is fully ingested before creating cc for the asset.';
        if (extension !== 'mp4') {
          message = 'Cannot create cc for a subclip for a .mxf, .wmv, or .mkv file until it is fully ingested';
        }
        alert(message, 'error');
        return;
      }

      setProgress(true, 'Please wait...');
      const assetId = assetForm.originalAssetData.id;
      FuelPortalService.createCaption(assetId, language).then((response) => {
        setTimeout(() => {
          FuelPortalService.getClipById(assetId).then((clip) => {
            if (clip.data.ingestStatus === 'Completed') {
              setProgress(false);
            }
            alert('Auto Create Captions initiated.  This process may take several minutes to complete.');
          });
        }, 5000);
      });
    }
  };
  const sendDownloadWarningMessage = (downloadUrl: string) => {
    if (!downloadUrl) {
      alert('There was an error retrieving your caption.  Please contact support for more information.', 'error');
    }
  };
  const downloadCaption = (subtitle: LinkedFileInfo) => {
    // console.log('download', {subtitle})
    let downloadUrl = '';
    try {
      if (!subtitle.publicUrl) {
        alert('The source file is not available for download!.', 'error');
        return;
      }
      downloadUrl = subtitle.publicUrl;
      // send a message out before attempting to download the item
      alert('Downloading file...');
      if (downloadUrl) {
        // try to download the asset, and if that doesn't work then try again after a second.
        setTimeout(() => {
          const win = window.open(downloadUrl, '_blank');
          if (win != null) {
            win.focus();
          }
        }, 1000);
      } else {
        console.error('Some error occured getting download link for', { subtitle });
        sendDownloadWarningMessage(downloadUrl);
      }
    } catch (e) {
      console.error(e);
      sendDownloadWarningMessage(downloadUrl);
    }
  };

  useEffect(() => {
    console.log({ assetForm });
    init();
  }, [assetForm]);

  return (
    <>
      <VnModalBody>
        <Container className="main-container" section="component" maxWidth="md" sx={{ padding: 0 }}>
          <Grid container className="section-box">
            <Grid container>
              <Grid item xs={5} sx={{ borderRight: '2px solid gray', borderStyle: 'dotted' }}>
                <input
                  className="file-input"
                  ref={fileInput}
                  id="uploalCaption"
                  type="file"
                  onChange={(e) => processFileChange(e.target.files)}
                />
                <div className="styled-table-buttons">
                  <i
                    title="Refresh CC"
                    test-id="cc-refresh-icon"
                    aria-hidden="true"
                    className="icon material-icons"
                    onClick={onRefreshClick}
                  >
                    refresh
                  </i>
                </div>
                <table className="styled-table">
                  <thead>
                    <tr>
                      <th>Language</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {externalSubtitles.map((subtitle: LinkedFileInfo, index) => {
                      return (
                        <tr key={index}>
                          <td>
                            <FormControlLabel
                              className={subtitle.publicUrl ? 'language-label bold' : 'language-label'}
                              checked={language === subtitle.description}
                              onChange={handleChange}
                              value={subtitle.description}
                              control={<Radio size="small" />}
                              label={subtitle.description}
                            />
                          </td>
                          <td>
                            <form className="caption-icons" encType="multipart/form-data">
                              <span
                                className={subtitle.publicUrl ? 'cc-modal-icon' : 'cc-modal-icon disable'}
                                title="Edit captions"
                                onClick={() => editCaptions(index)}
                              >
                                <i
                                  aria-hidden="true"
                                  className={subtitle.publicUrl ? 'icon material-icons' : 'icon material-icons disable'}
                                >
                                  mode_edit
                                </i>
                              </span>
                              <span
                                className={subtitle.publicUrl ? 'cc-modal-icon disable' : 'cc-modal-icon'}
                                title="Upload captions"
                                onClick={(evt) => onBrowseButtonClick(evt, subtitle)}
                              >
                                <i
                                  aria-hidden="true"
                                  className={subtitle.publicUrl ? 'icon material-icons disable' : 'icon material-icons'}
                                >
                                  upload
                                </i>
                              </span>
                              <span
                                title={
                                  allowCreateCaptions()
                                    ? 'Create captions'
                                    : 'Please wait until asset is fully ingested before creating cc for the asset'
                                }
                                className={allowCreateCaptions() ? 'cc-modal-icon' : 'cc-modal-icon disable'}
                                onClick={() => createCaption()}
                              >
                                <i
                                  aria-hidden="true"
                                  className={
                                    allowCreateCaptions()
                                      ? 'icon ion-wand custom-wand-icon'
                                      : 'icon ion-wand custom-wand-icon disable'
                                  }
                                />
                              </span>
                              <span
                                className={subtitle.publicUrl ? 'cc-modal-icon' : 'cc-modal-icon disable'}
                                title="Download"
                                onClick={() => downloadCaption(subtitle)}
                              >
                                <i
                                  aria-hidden="true"
                                  className={subtitle.publicUrl ? 'icon material-icons' : 'icon material-icons disable'}
                                >
                                  download
                                </i>
                              </span>
                              <span
                                className={subtitle.publicUrl ? 'cc-modal-icon' : 'cc-modal-icon disable'}
                                title="Delete captions"
                                onClick={() => deleteCaptions(index)}
                              >
                                <i
                                  aria-hidden="true"
                                  className={subtitle.publicUrl ? 'icon material-icons' : 'icon material-icons disable'}
                                >
                                  delete
                                </i>
                              </span>
                            </form>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </Grid>
              <Grid item xs={7} className="captions-text-container">
                {/* <textarea rows="10" value={fileText}></textarea> */}
                <TextField
                  className="captions-text"
                  variant="outlined"
                  onChange={(e) => setFileText(e.target.value)}
                  value={fileText}
                  fullWidth
                  multiline
                  minRows={5}
                  disabled={!isEditMode}
                  sx={{ marginTop: 1 }}
                />
              </Grid>
            </Grid>
          </Grid>
        </Container>
      </VnModalBody>
      <VnModalFooter
        right={
          <>
            <Button autoFocus variant="contained" className="cancel-button" onClick={handleClose}>
              Close
            </Button>
            {isEditMode && (
              <>
                <Button
                  autoFocus
                  color="primary"
                  variant="contained"
                  className={isEditMode ? '' : 'hide-save-button'}
                  onClick={saveCaptions}
                >
                  Save Captions
                </Button>
                <Button autoFocus variant="contained" className={isEditMode ? '' : 'hide-save-button'} onClick={cancel}>
                  Cancel
                </Button>
              </>
            )}
          </>
        }
      />
    </>
  );
};

export default CloseCaptionModal;
